<template>
  <v-main>
    <app-navigation />
    <!-- Main Form -->
    <v-sheet class="ukpl-blue-card-container" height="100%">
      <v-container fluid height="100%">
        <v-form ref="form" class="mx-2" lazy-validation>
          <v-row justify="center" align="center" class="row">
            <v-col cols="12">
              <h1 class="h1-heading">
                Please select the division and team you will be playing in for the {{ new Date().getFullYear() }}
                season.
              </h1>
              <br />

              <p class="text-left">
                NOTE: If you can't locate your team in the division list,
                please get your team captain to contact us either on fb
                messenger or
                <a style="color: #c1c1c1" href="mailto:admin@ukpl.co.uk">admin@ukpl.co.uk</a>
                to register your team.
              </p>
            </v-col>
          </v-row>
          <!-- League Selection -->
          <v-row justify="center" align="center" class="row">
            <v-col cols="12" sm="12" md="12" xs="12" class="text-center">
              <v-select
                v-model="selectedLeagues"
                :items="leagues"
                item-text="leagueName"
                item-value="id"
                label="Select Leagues" 
                multiple
                chips
                dense
                return-object
              ></v-select>
            </v-col>
          </v-row>

          <!-- Team Selection Per League -->
          <div v-for="league in selectedLeagues" :key="league.id">
            <v-row justify="center" align="center" class="row">
                <v-col cols="12">
                    <h2 class="h2-heading" style="color: white;">{{ league.leagueName }}</h2>
                </v-col>
            </v-row>
            <v-row justify="center" align="center" class="row">
                <v-col cols="12" sm="12" md="12" xs="12" class="text-center">
                <v-select
                    v-model="userLeagueSelections[league.id]"
                    :items="leagueTeams[league.id]"
                    item-text="combinedName"
                    item-value="combinedId"
                    label="Select Team/Division"
                    dense
                    :rules="rules"
                    return-object
                    @change="updateDivTeamForLeague(league.id, $event)"
                ></v-select>
                <div v-if="userLeagueSelections[league.id]" class="mt-6 mx-auto" style="max-width: 600px;">
                  <v-alert
                    outlined
                    type="error"
                    prominent
                    border="left"
                  >
                    <v-row align="center">
                      <v-col class="grow text-left">
                        <div class="title mb-1">Team Captain?</div>
                        <div class="subtitle-2">Please <strong>ONLY</strong> check this box if you are the official team captain.</div>
                      </v-col>
                      <v-col class="shrink">
                        <v-checkbox
                            v-model="leagueCaptains[league.id]"
                            color="error"
                            hide-details
                            class="mt-0"
                        ></v-checkbox>
                      </v-col>
                    </v-row>
                  </v-alert>
                </div>
                </v-col>
            </v-row>
          </div>

          <!-- Disclaimers Dynamic Blocks -->
          <v-expansion-panels v-model="activePanel" accordion class="mb-6" id="disclaimers-container">
            <v-expansion-panel v-for="(league, i) in selectedLeagues" :key="'disc_'+league.id" :id="'panel-' + i">
              <v-expansion-panel-header>
                <div>
                  <v-icon v-if="leagueDisclaimers[league.id]" color="success" class="mr-2">mdi-check-circle</v-icon>
                  <v-icon v-else color="grey" class="mr-2">mdi-circle-outline</v-icon>
                  <span :class="{'green--text': leagueDisclaimers[league.id]}">
                    <strong>{{ league.leagueName }} Disclaimer</strong>
                    <span v-if="leagueDisclaimers[league.id]"> - Accepted</span>
                  </span>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="league.leagueName.toUpperCase().includes('CPPS') || league.leagueName.toUpperCase().includes('OKPB')">
                  <h1 class="h1-heading"><strong>CPPS / OKPB {{ currentyear }} Participant Declaration</strong></h1>
                  <h2 class="h2-heading"><strong>Season Participation Agreement: {{ currentyear }}</strong></h2>
                  <br/>
                  <p>By signing this declaration, you confirm that you have read, understood and agree to the following conditions for the {{ currentyear }} season.</p>
                  <p>You agree to attend and listen to all safety briefings and to comply fully with all event rules, referee instructions and safety procedures at all times.</p>
                  
                  <p><strong>1. Assumption of Risk</strong></p>
                  <p>Paintball is a physically demanding and competitive sport. Participation may result in minor injuries including bruising, abrasions, strains or impact injuries. Paintballs discharged at close range may cause temporary discomfort or break the skin.<br/>
                  By participating in CPPS / OKPB Ltd events, you acknowledge and accept the inherent risks associated with the sport.</p>
                  
                  <p><strong>2. Conduct & Behaviour</strong></p>
                  <p>While attending any CPPS / OKPB organised event, you agree to:</p>
                  <ul>
                    <li>Conduct yourself in a respectful and appropriate manner</li>
                    <li>Follow all referee and staff instructions immediately</li>
                    <li>Not engage in behaviour that endangers yourself or others</li>
                    <li>Not enter restricted or staff-only areas without permission</li>
                  </ul>
                  <p>CPPS / OKPB Ltd reserves the right to remove or refuse participation to any individual whose behaviour is deemed unsafe, disruptive, abusive, or inappropriate.</p>
                  
                  <p><strong>3. Equipment Responsibility</strong></p>
                  <p>You confirm that:</p>
                  <ul>
                    <li>All equipment used by you is fit for purpose and compliant with current UK safety standards</li>
                    <li>All air systems are in valid hydrostatic test and meet UK regulations</li>
                    <li>You understand that CPPS / OKPB operates a self-fill air system and you are responsible for ensuring your system is safe and in date</li>
                  </ul>
                  <p>Event staff reserve the right to inspect equipment at any time.</p>
                  
                  <p><strong>4. Liability & Insurance</strong></p>
                  <p>OKPB Ltd holds appropriate Public Liability Insurance for organised events.<br/>
                  However:</p>
                  <ul>
                    <li>You participate at your own risk</li>
                    <li>OKPB Ltd cannot accept liability for injury, loss or damage resulting from risks inherent to the sport</li>
                    <li>Nothing in this declaration excludes liability for death or personal injury caused by negligence</li>
                  </ul>
                  <p>You are responsible for your own personal insurance cover if desired.</p>
                  
                  <p><strong>5. Property</strong></p>
                  <p>CPPS / OKPB Ltd accepts no responsibility for loss, theft or damage to personal belongings brought onto site.</p>
                  
                  <p><strong>6. Alcohol & Substance Policy</strong></p>
                  <p>Participants must not attend or compete under the influence of alcohol or illegal substances.<br/>
                  CPPS / OKPB Ltd reserves the right to refuse participation to anyone believed to be impaired.</p>
                  
                  <p><strong>7. Media & Photography</strong></p>
                  <p>By attending CPPS / OKPB events, you consent to photographs and video footage being captured for promotional, media and marketing purposes.<br/>
                  All media remains the property of CPPS / OKPB Ltd.<br/>
                  If you have concerns regarding media use, you must notify event management in writing.</p>
                  
                  <p><strong>8. Emergency Contact</strong></p>
                  <p>You confirm that the emergency contact details provided are correct.<br/>
                  In the event of injury, you authorise event staff to seek medical assistance if required.</p>
                  
                  <p><strong>Under 18 Participants – Parental Consent</strong></p>
                  <p>If the participant is under 18 years of age, this section must be completed by a parent or legal guardian.<br/>
                  I confirm that I am the parent or legal guardian of the named participant and give permission for them to participate in CPPS / OKPB events for the {{ currentyear }} season.<br/>
                  I acknowledge that paintball carries inherent physical risks and confirm that the participant is medically fit to take part.<br/>
                  I understand that CPPS / OKPB Ltd will take reasonable steps to ensure safety but that participation is undertaken at the participant’s own risk.</p>
                  
                  <p><strong>Final Declaration</strong></p>
                  <p>I confirm that:</p>
                  <ul>
                    <li>I have read and understood this agreement</li>
                    <li>I accept the risks associated with participation</li>
                    <li>I agree to comply with all safety rules and event regulations</li>
                    <li>The information I have provided is accurate</li>
                  </ul>
                  <br/>
                  
                  <template v-if="userDetails.isOver18">
                    <v-checkbox v-model="leagueDisclaimers[league.id]" :rules="mandatoryRules" label="I have read and accepted the CPPS / OKPB disclaimer" @change="handleDisclaimerCheck(i)">
                    </v-checkbox>
                  </template>
                  <template v-else>
                    <div style="background-color: rgba(255,0,0,0.1); padding: 15px; border-left: 4px solid red; margin-bottom: 20px;">
                      <h3 style="color: red; margin-bottom: 10px;">Under 18 Participants: Parental Consent</h3>
                      <p>If the participant is under 18 years of age, this section must be completed by a parent or legal guardian.</p>
                      <p>I confirm that I am the parent or legal guardian of the named participant and give permission for them to participate in {{ league.leagueName }} events during the {{ currentyear }} season.</p>
                      <p>I acknowledge that paintball is a physically demanding activity involving inherent risks, including the risk of injury, and confirm that the participant is medically fit to take part.</p>
                      <p>I understand that {{ league.leagueName }} will take reasonable steps to ensure safety, but participation is undertaken at the participant’s own risk.</p>
                      <p>I confirm that the participant will comply with all safety rules, event regulations, and staff instructions at all times.</p>
                    </div>
                    <v-checkbox v-model="leagueDisclaimers[league.id]" :rules="mandatoryRules" label="I confirm that I have read, understood and agree to the above on behalf of the participant." @change="handleDisclaimerCheck(i)">
                    </v-checkbox>
                  </template>
                  
                </div>
                <div v-else-if="league.leagueName.toUpperCase().includes('ECPL')">
                  <h1 class="h1-heading"><strong>ECPL Season Participation Agreement: {{ currentyear }}</strong></h1>
                  <br/>
                  <p>By signing this declaration, you confirm that you have read, understood and agree to the following terms for participation in events organised by the European Classic Paintball League (ECPL).<br/>
                  You agree to comply with all safety briefings, event rules and staff instructions at all times.</p>
                  
                  <p><strong>1. Acknowledgement of Risk</strong></p>
                  <p>I understand that participation in competitive paintball involves inherent physical risks.<br/>
                  These include, but are not limited to:</p>
                  <ul>
                    <li>Uneven ground, fallen trees, natural woodland hazards</li>
                    <li>Slips, trips and falls</li>
                    <li>Impact from paintballs, which may cause bruising or occasional minor skin breakage</li>
                  </ul>
                  <p>I accept that I participate at my own risk.</p>
                  
                  <p><strong>2. Conduct & Professional Boundaries</strong></p>
                  <p>I agree to:</p>
                  <ul>
                    <li>Conduct myself in an appropriate and respectful manner</li>
                    <li>Follow all referee and staff instructions immediately</li>
                    <li>Not enter restricted or staff-only areas without permission</li>
                    <li>Not behave in a way that endangers myself, other players, staff or spectators</li>
                  </ul>
                  <p>ECPL reserves the right to refuse admission, suspend or remove any participant whose behaviour is considered unsafe, disruptive, abusive or inappropriate.</p>
                  
                  <p><strong>3. Alcohol & Substance Policy</strong></p>
                  <p>Participants must not attend or compete under the influence of alcohol or illegal substances.<br/>
                  ECPL reserves the right to refuse participation or remove any individual suspected of being impaired. Breathalysing may be used where necessary.</p>
                  
                  <p><strong>4. Equipment & Personal Responsibility</strong></p>
                  <p>I confirm that:</p>
                  <ul>
                    <li>My equipment is fit for purpose and compliant with UK safety standards</li>
                    <li>My air system is in valid test and meets UK regulations</li>
                    <li>I am responsible for ensuring my equipment is safe and correctly maintained</li>
                  </ul>
                  
                  <p><strong>5. Liability</strong></p>
                  <p>ECPL will take reasonable steps to ensure participant safety and fulfil its duty of care.<br/>
                  However:</p>
                  <ul>
                    <li>Participation is undertaken at my own risk</li>
                    <li>ECPL and landowners accept no responsibility for injury, loss or damage arising from risks inherent to the sport</li>
                    <li>Nothing in this agreement excludes liability for death or personal injury caused by negligence</li>
                  </ul>
                  
                  <p><strong>6. Property</strong></p>
                  <p>ECPL accepts no responsibility for loss, theft or damage to personal belongings brought onto site.</p>
                  
                  <p><strong>7. Media & Photography</strong></p>
                  <p>I understand that photographs and video footage may be captured during events for promotional, media or marketing purposes.<br/>
                  ECPL retains full rights to such media.<br/>
                  If I have concerns regarding media usage, I must notify event management in writing.</p>
                  
                  <p><strong>8. Clarification & Withdrawal</strong></p>
                  <p>If, following any safety briefing, I require clarification or no longer wish to participate, I will inform the Event Manager and withdraw from participation immediately.</p>
                  
                  <p><strong>Under 18 Participants: Parental Consent</strong></p>
                  <p>If the participant is under 18 years of age, this section must be completed by a parent or legal guardian.<br/>
                  I confirm that I am the parent or legal guardian of the named participant and give permission for them to participate in ECPL events during the {{ currentyear }} season.<br/>
                  I acknowledge that paintball involves inherent physical risks and confirm that the participant is medically fit to take part.<br/>
                  I understand that ECPL will take reasonable steps to ensure safety but that participation is undertaken at the participant’s own risk.<br/>
                  I confirm that the participant will comply with all safety rules and event regulations.</p>
                  
                  <p><strong>Final Declaration</strong></p>
                  <p>I confirm that:</p>
                  <ul>
                    <li>I have read and understood this agreement</li>
                    <li>I accept the risks associated with participation</li>
                    <li>I agree to comply with all ECPL rules and safety procedures</li>
                    <li>The information provided is accurate</li>
                  </ul>
                  <br/>

                  <template v-if="userDetails.isOver18">
                    <v-checkbox v-model="leagueDisclaimers[league.id]" :rules="mandatoryRules" label="I have read and accepted the ECPL disclaimer" @change="handleDisclaimerCheck(i)">
                    </v-checkbox>
                  </template>
                  <template v-else>
                    <div style="background-color: rgba(255,0,0,0.1); padding: 15px; border-left: 4px solid red; margin-bottom: 20px;">
                      <h3 style="color: red; margin-bottom: 10px;">Under 18 Participants: Parental Consent</h3>
                      <p>If the participant is under 18 years of age, this section must be completed by a parent or legal guardian.</p>
                      <p>I confirm that I am the parent or legal guardian of the named participant and give permission for them to participate in {{ league.leagueName }} events during the {{ currentyear }} season.</p>
                      <p>I acknowledge that paintball is a physically demanding activity involving inherent risks, including the risk of injury, and confirm that the participant is medically fit to take part.</p>
                      <p>I understand that {{ league.leagueName }} will take reasonable steps to ensure safety, but participation is undertaken at the participant’s own risk.</p>
                      <p>I confirm that the participant will comply with all safety rules, event regulations, and staff instructions at all times.</p>
                    </div>
                    <v-checkbox v-model="leagueDisclaimers[league.id]" :rules="mandatoryRules" label="I confirm that I have read, understood and agree to the above on behalf of the participant." @change="handleDisclaimerCheck(i)">
                    </v-checkbox>
                  </template>

                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <template v-if="userDetails.isOver18">
            <v-row>
              <v-col cols="6">
                <v-text-field v-model="userDetails.emergencycontactname" :rules="mandatoryRules"
                  label="Emergency Contact Name" dense>
                </v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="userDetails.emergencycontactnumber" :rules="mandatoryRules"
                  label="Emergency Contact Number" dense>
                </v-text-field>
              </v-col>
            </v-row>
          </template>
          <template v-else>
            <div style="margin-top: 30px; margin-bottom: 20px;">
              <h2 class="h2-heading">Parent / Guardian Consent (Required for Under 18 Participants)</h2>
              <h3 class="h3-heading" style="color: grey;">Parent / Guardian Details</h3>
            </div>
            
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="userDetails.emergencycontactname" :rules="mandatoryRules"
                  label="Full Name of Parent / Legal Guardian" dense>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="userDetails.parentRelationship" :rules="mandatoryRules"
                  label="Relationship to Participant" dense>
                </v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="userDetails.parentContactNumber" :rules="mandatoryRules"
                  label="Contact Telephone Number" dense>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="userDetails.parentEmail" :rules="[...mandatoryRules, v => /.+@.+\..+/.test(v) || 'E-mail must be valid']"
                  label="Email Address" dense>
                </v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="userDetails.emergencycontactnumber" :rules="mandatoryRules"
                  label="Emergency Contact Number (if different)" dense>
                </v-text-field>
              </v-col>
            </v-row>
          </template>

          <v-row justify="center" align="center" class="row">
            <v-col cols="6" sm="6" md="6" xs="6 " class="text-center">
              <v-btn class="red-btn-primary" rounded dark @click="save">Next</v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </v-sheet>
    <v-snackbar :timeout="2000" v-model="messageon" color="red accent-4" rounded="pill" elevation="24">
      {{ message }}
    </v-snackbar>
    <v-snackbar :timeout="4000" v-model="savefail" color="red accent-4" rounded="pill" elevation="24">
      {{ failmessage }}
    </v-snackbar>
    <v-snackbar :timeout="1000" v-model="savesuccess" color="green accent-4" rounded="pill" elevation="24">
      Details Saved.
    </v-snackbar>
  </v-main>
</template>

<script>
import UserService from "../services/user.service";
import UserDetails from "../model/userdetails";
import DivisionService from "../services/division.service";
import UtilService from "../services/util.service";

import AppNavigation from "../components/AppNavigation";

export default {
  name: "Disclaimer",
  components: {
    AppNavigation,
  },
  data: () => ({
    messageon: false,
    savesuccess: "",
    savefail: false,
    failmessage: "",
    message: "",
    leagueDisclaimers: {},
    leagueCaptains: {},
    activePanel: undefined,
    userDetails: new UserDetails(),
    mandatoryRules: [],
    divisionsteams: [],
    selectdiv: "1",
    selectteam: "",
    selectdivteam: "",
    rules: [],
    currentyear: 0,
    leagues: [],
    selectedLeagues: [],
    leagueTeams: {},
    userLeagueSelections: {},
  }),
  mounted() {
    this.mandatoryRules = [(v) => !!v || "Field is required"];
    this.rules = [(v) => {
      if (!v) {
        return "please select a value.";
      }
      if ((v.combinedId && /[0-9]+\|[0-9]+/.test(v.combinedId)) || (typeof v === 'string' && /[0-9]+\|[0-9]+/.test(v))) {
        return true;
      }
      return "please select a value.";
    }];
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
  },
  methods: {
    save() {
      if (!this.$refs.form.validate()) {
        return false;
      }
      
      // Validate that a team is selected for each league
      let allLeaguesValid = true;
      if (this.selectedLeagues.length === 0) {
          allLeaguesValid = false;
          this.message = "Please select at least one league.";
          this.messageon = true;
          return;
      }

      this.selectedLeagues.forEach(league => {
          if (!this.userLeagueSelections[league.id]) {
              allLeaguesValid = false;
          }
      });

      if (!allLeaguesValid) {
          this.message = "Please select a team for each league.";
          this.messageon = true;
          return;
      }
      
      this.selectedLeagues.forEach(league => {
          if (!this.leagueDisclaimers[league.id]) {
              allLeaguesValid = false;
          }
      });

      if (!allLeaguesValid) {
          this.message = "Please read and accept all relevant league disclaimers before proceeding.";
          this.messageon = true;
          return;
      }

      console.log("form verified, saving.");

      // Construct league selections payload
      const leagueSelections = this.selectedLeagues.map(league => {
          const selection = this.userLeagueSelections[league.id];
          return {
              leagueId: league.id,
              leagueName: league.leagueName,
              divisionId: selection ? selection.divId : null,
              teamId: selection ? selection.teamId : null,
              disclaimer: !!this.leagueDisclaimers[league.id],
              isCaptain: !!this.leagueCaptains[league.id]
          };
      });
      
      this.userDetails.leagueSelections = leagueSelections;
      
      // Set legacy overall payload disclaimer field to true if at least one checkbox is checked
      if (leagueSelections.some(l => l.disclaimer)) {
          this.userDetails.disclaimer = true;
          this.userDetails.disclaimerdate = new Date();
      } else {
          this.userDetails.disclaimer = false;
          this.userDetails.disclaimerdate = null;
      }

      // Keep legacy support if needed, or maybe just take the first one?
      // For now, I'll update the first one found just in case backend still relies on it for something else
      if (leagueSelections.length > 0) {
          this.userDetails.divid = leagueSelections[0].divisionId;
      }

      console.log("Saving User Details Payload:", JSON.stringify(this.userDetails, null, 2));

      UserService.signDisclaimer(this.userDetails).then(
        (response) => {
          this.message = response.message;
          this.registersuccess = true;
          this.savesuccess = true;
        },
        (error) => {
          this.failmessage =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            error.toString();
          this.savefail = true;
        }
      );
    },
    initializeDivTeam() {
      DivisionService.getDivisionsTeams().then(
        (response) => {
          this.divisionsteams = response.data;
        },
        (error) => {
          this.message =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            error.toString();
        }
      );
    },
    updateDivTeamForLeague(leagueId, selObj) {
      if(selObj){
        this.$set(this.userLeagueSelections, leagueId, selObj);
      }
    },
    fetchLeagues() {
      UtilService.getLeagues().then((response) => {
        this.leagues = response.data;
      });
    },
    fetchTeamsForLeague(leagueId) {
      if (!this.leagueTeams[leagueId]) {
        DivisionService.getDivisionsTeams(leagueId).then((response) => {
          this.$set(this.leagueTeams, leagueId, response.data);
        });
      }
    },
    initCurrentYear() {
      UtilService.getCurrentYear().then(
        (response) => {
          this.currentyear = response.data.year;
        },
        (error) => {
          this.message =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            error.toString();
        }
      );
    },
    handleDisclaimerCheck(index) {
        try {
          // Find the next unticked box
          let nextUnticked = -1;
          for (let i = index + 1; i < this.selectedLeagues.length; i++) {
              if (!this.leagueDisclaimers[this.selectedLeagues[i].id]) {
                  nextUnticked = i;
                  break;
              }
          }

          // Wrap around if we didn't find one
          if (nextUnticked === -1) {
              for (let i = 0; i < index; i++) {
                  if (!this.leagueDisclaimers[this.selectedLeagues[i].id]) {
                      nextUnticked = i;
                      break;
                  }
              }
          }

          // If we found an unticked box, step to it
          if (nextUnticked !== -1) {
              this.activePanel = nextUnticked;

              // Scroll to the top of the newly opened panel after a short delay for the DOM to update
              setTimeout(() => {
                  try {
                    const element = document.getElementById('panel-' + nextUnticked);
                    // Guard: element must be in the document (not detached/navigated away)
                    if (element && document.contains(element)) {
                        const y = element.getBoundingClientRect().top + window.scrollY - 100;
                        window.scrollTo({ top: y, behavior: 'smooth' });
                    }
                  } catch (scrollErr) {
                    // Silently swallow scroll errors (e.g. InvalidAccessError on iOS Safari)
                    console.warn('Scroll to panel failed:', scrollErr);
                  }
              }, 300);
          } else {
              // Otherwise, close all panels so they can advance
              this.activePanel = undefined;
          }
        } catch (err) {
          console.warn('handleDisclaimerCheck error:', err);
        }
    }
  },
  async created() {
    try {
      const yearResp = await UtilService.getCurrentYear();
      this.currentyear = yearResp.data.year;

      const leaguesResp = await UtilService.getLeagues();
      this.leagues = leaguesResp.data;

      const userResp = await UserService.getUserDetails();
      const userData = userResp.data;

      this.userDetails.username = userData.username;
      this.userDetails.email = userData.email;
      this.userDetails.password = userData.password;
      this.userDetails.firstname = userData.firstname;
      this.userDetails.lastname = userData.lastname;
      this.userDetails.gender = userData.gender;
      this.userDetails.isOver18 = userData.isOver18;
      this.userDetails.contactNumber = userData.contactNumber;
      this.userDetails.addressline1 = userData.addressline1;
      this.userDetails.addressline2 = userData.addressline2;
      this.userDetails.town = userData.town;
      this.userDetails.county = userData.county;
      this.userDetails.postcode = userData.postcode;
      this.userDetails.file = userData.file;
      this.userDetails.ukpsf = userData.ukpsf;
      this.userDetails.teamcaptain = userData.teamcaptain;
      this.userDetails.disclaimer = userData.disclaimer;
      this.userDetails.disclaimerdate = userData.disclaimerDate;
      this.userDetails.emergencycontactname = userData.emergencyContactName;
      this.userDetails.emergencycontactnumber = userData.emergencyContactNumber;
      this.userDetails.parentRelationship = userData.parentRelationship;
      this.userDetails.parentContactNumber = userData.parentContactNumber;
      this.userDetails.parentEmail = userData.parentEmail;

      if (userData.userHistory && userData.userHistory.length > 0) {
        let loadedLeagues = [];
        let loadedSelections = {};

        for (const history of userData.userHistory) {
          if (this.currentyear === parseInt(history.year) && history.division && history.division.leagueId) {
            
            // Use == (not ===) to handle potential Long vs Number type mismatch between leagues list and history
            const leagueObj = this.leagues.find(l => l.id == history.division.leagueId);
            if (leagueObj) {
              if (!loadedLeagues.some(l => l.id === leagueObj.id)) {
                loadedLeagues.push(leagueObj);
              }
              
              // Fetch teams for this league if not already fetched
              if (!this.leagueTeams[leagueObj.id]) {
                const teamsResp = await DivisionService.getDivisionsTeams(leagueObj.id);
                this.$set(this.leagueTeams, leagueObj.id, teamsResp.data);
              }

              if (!history.team) {
                console.warn('[Disclaimer] history.team is null for history entry', history);
              } else {
                const targetCombinedId = history.division.id + "|" + history.team.id;
                const selectionObj = this.leagueTeams[leagueObj.id].find(t => t.combinedId === targetCombinedId);
                if (selectionObj) {
                  loadedSelections[leagueObj.id] = selectionObj;
                }
              }
              // Hydrate disclaimer state per league
              if (history.disclaimer) {
                this.$set(this.leagueDisclaimers, leagueObj.id, history.disclaimer);
              }
            }
          }
        }
        
        // Assign arrays to data
        this.selectedLeagues = loadedLeagues;

        // Ensure selections are applied after watcher might have fired and set them to null
        this.$nextTick(() => {
          for (const [leagueId, selObj] of Object.entries(loadedSelections)) {
            this.$set(this.userLeagueSelections, leagueId, selObj);
          }
          
          for (const history of userData.userHistory) {
              if (this.currentyear === parseInt(history.year) && history.division && history.division.leagueId) {
                  this.$set(this.leagueCaptains, history.division.leagueId, history.isCaptain);
              }
          }
        });
      }
    } catch (error) {
      this.message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      this.messageon = true;
      console.error('[Disclaimer] created() error:', error);
    }
  },
  watch: {
    selectedLeagues(newVal) {
      // Find the first panel that isn't ticked when leagues are selected
      if (newVal && newVal.length > 0) {
        let firstUnticked = newVal.findIndex(l => !this.leagueDisclaimers[l.id]);
        this.activePanel = firstUnticked !== -1 ? firstUnticked : undefined;
      }
      
      if (newVal) {
        newVal.forEach(league => {
          this.fetchTeamsForLeague(league.id);
          if (!Object.prototype.hasOwnProperty.call(this.userLeagueSelections, league.id)) {
              this.$set(this.userLeagueSelections, league.id, null);
          }
        });
      }
    },
    savesuccess: function () {
      if (this.savesuccess === false) {
        this.$router.push("/idcard");
      }
    }
  },
};
</script>
